import {
  Locale,
  addDays,
  addWeeks,
  differenceInDays,
  format,
  isBefore,
  isSameMonth,
  isSameYear,
  nextSaturday,
  startOfDay,
  startOfToday,
  startOfWeek,
} from 'date-fns'
import { de, enGB, fr, it } from 'date-fns/locale'
import compact from 'lodash/compact'

import { AppLocale } from '@procsea/common/utils'

import { ShippableIsoWeekDays, WeekDays } from 'src/types'

export const dateLocales: Record<AppLocale, Locale> = {
  fr,
  it,
  de,
  en: enGB,
}

export const getDaysDiff = (dateFrom: Date, dateTo: Date) =>
  differenceInDays(startOfDay(dateFrom), startOfDay(dateTo))

export const getDaysFromNow = (date: string) => differenceInDays(new Date(date), new Date())

export const getDayOfUpcomingWeek = (isoDay: WeekDays) =>
  addDays(addWeeks(startOfWeek(startOfToday()), 1), isoDay)

export const isDatePassed = (date: Date, referenceDate = new Date()) =>
  isBefore(date, referenceDate)

type DateFormatKey =
  | 'dateLocaleFormat'
  | 'dateTimeUpdateFormat'
  | 'dateTimeWithPeriod'
  | 'dateTimeWithYearAndPeriod'
  | 'defaultDate'
  | 'extendedFormat'
  | 'mobileTimelineDate'
  | 'shortDateFormat'
  | 'shortDateWithDay'
  | 'timelineDate'
  | 'timeLocaleFormat'
  | 'weekDayName'

export const DATES_FORMAT: Record<DateFormatKey, string> = {
  dateLocaleFormat: 'P',
  dateTimeUpdateFormat: 'dd/MM/yy - p',
  dateTimeWithPeriod: 'EEE dd MMM p',
  dateTimeWithYearAndPeriod: 'EEE dd MMM yyyy p',
  defaultDate: 'yyyy-MM-dd',
  extendedFormat: 'EEEE d MMMM',
  mobileTimelineDate: 'dd MMM',
  shortDateFormat: 'dd/MM',
  shortDateWithDay: 'EEE d MMM',
  timelineDate: 'EEE dd MMM',
  timeLocaleFormat: 'p',
  weekDayName: 'EEEE',
}

export const formatWithLocale = (
  date: Date | ApiDate,
  formatPattern = DATES_FORMAT.defaultDate,
  locale?: string
) => {
  switch (locale) {
    case 'fr':
      return format(date, formatPattern, { locale: fr })
    case 'it':
      return format(date, formatPattern, { locale: it })
    case 'de':
      return format(date, formatPattern, { locale: de })
    case 'en':
      return format(date, formatPattern, { locale: enGB })
    default:
      return format(date, formatPattern)
  }
}

export const getIsoWeekDaysFromSPI = (spi: ShippableIsoWeekDays): WeekDays[] =>
  compact([
    spi.canBeShippedOnIsoWeekday1 && WeekDays.MONDAY,
    spi.canBeShippedOnIsoWeekday2 && WeekDays.TUESDAY,
    spi.canBeShippedOnIsoWeekday3 && WeekDays.WEDNESDAY,
    spi.canBeShippedOnIsoWeekday4 && WeekDays.THURSDAY,
    spi.canBeShippedOnIsoWeekday5 && WeekDays.FRIDAY,
    spi.canBeShippedOnIsoWeekday6 && WeekDays.SATURDAY,
    spi.canBeShippedOnIsoWeekday7 && WeekDays.SUNDAY,
  ])

export const getSPIPayloadFromWeekDays = (
  weekDays?: WeekDays[]
): Partial<ShippableIsoWeekDays> => ({
  canBeShippedOnIsoWeekday1: weekDays?.includes(WeekDays.MONDAY),
  canBeShippedOnIsoWeekday2: weekDays?.includes(WeekDays.TUESDAY),
  canBeShippedOnIsoWeekday3: weekDays?.includes(WeekDays.WEDNESDAY),
  canBeShippedOnIsoWeekday4: weekDays?.includes(WeekDays.THURSDAY),
  canBeShippedOnIsoWeekday5: weekDays?.includes(WeekDays.FRIDAY),
  canBeShippedOnIsoWeekday6: weekDays?.includes(WeekDays.SATURDAY),
  canBeShippedOnIsoWeekday7: weekDays?.includes(WeekDays.SUNDAY),
})

export type OpenDays = [ApiDate, ApiDate, ApiDate, ApiDate, ApiDate, ApiDate]

export const displayMonthOfWeek = (week: Date, locale: AppLocale) => {
  const monday = startOfWeek(week, { weekStartsOn: 1 })
  const saturday = nextSaturday(monday)

  if (isSameMonth(monday, saturday)) {
    return formatWithLocale(week, 'MMMM y', locale)
  }
  if (isSameYear(monday, saturday)) {
    return `${formatWithLocale(monday, 'MMM', locale)} - ${formatWithLocale(
      saturday,
      'MMM',
      locale
    )} ${formatWithLocale(monday, 'y', locale)}`
  }
  return `${formatWithLocale(monday, 'MMM y', locale)} - ${formatWithLocale(
    saturday,
    'MMM y',
    locale
  )}`
}
